import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCreateSolarEstimationMutation,
  useInitiateSolarEstimationMutation,
  useLazyAdvancedSolarEstimationCalculationQuery,
  useLazyViewSolarEstimationQuery,
  useUpdateEstimationMutation,
} from "../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../redux/features/auth/auth-slice";
import { setBillCalculatorFiltersSpaceAndSpaceDetails } from "../../../redux/features/bill-calculator-filter/bill-calculator-filter-slice";
import {
  selectBillingSpaceFilter,
  selectNewClusterId,
  setBillingSpaceFilter,
} from "../../../redux/features/billing-space-filter/billing-space-filter-slice";
import { setSelectedFilterSpace } from "../../../redux/features/filter/filter-slice";
import { selectCountry } from "../../../redux/features/user/user-slice";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  IViewSolarUserSelectedProducts,
  ViewSolarEstimationResponseDTO,
} from "../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../shared/oversight-core/enums/application-type";
import { EEnergyNeedEditParameter } from "../../../shared/oversight-core/enums/energy-need-edit-parameters";
import { EEstimationType } from "../../../shared/oversight-core/enums/estimation-type";
import { EInverterPhaseType } from "../../../shared/oversight-core/enums/inverter-phase-type";
import { ESolarSolutionType } from "../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimBrands } from "../../../shared/oversight-core/enums/solar-sim-brands";
import { ESolarSimLocalStorage } from "../../../shared/oversight-core/enums/solar-sim-local-storage";
import { ESolarSystemType } from "../../../shared/oversight-core/enums/solar-system-type ";
import { EUtilityOptionType } from "../../../shared/oversight-core/enums/utility-option-type";
import { IAdvancedSave } from "../../../shared/oversight-core/interfaces/advanced-save";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { Configurations } from "../../../shared/oversight-core/interfaces/configurations";
import { IEstimatorDetails } from "../../../shared/oversight-core/interfaces/estimator-details";
import { Option } from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { ISteps } from "../../../shared/oversight-core/ui-elements/steps-number/steps-number";
import { offsetToString } from "../../../shared/oversight-core/utils/offsetToString";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import BillCalculator from "../../dashboard/bill-calculator/bill-calculator";
import AddUpdateBillCalculator, {
  IAddBillCalculatorProps,
} from "../../dashboard/bill-calculator/components/add-update-bill-calculator/add-update-bill-calculator";
import ApprovedSystem from "../components/approved-system/approved-system";
import AssembleSystem from "../components/assemble-system/assemble-system";
import EditParameterModal from "../components/edit-parameter-modal/edit-parameter-modal";
import IncludeSystemCard from "../components/include-system-card/include-system-card";
import RemoveSolarSystemModal from "../components/remove-solar-system-modal/remove-solar-system-modal";
import RemoveWaterHeaterSystemModal from "../components/remove-water-heater-system-modal/remove-water-heater-system-modal";
import SelectedSolarSystemCard from "../components/selected-solar-system-card/selected-solar-system-card";
import SelectedWaterHeaterSystemCard from "../components/selected-water-heater-system-card/selected-water-heater-system-card";
import Spaces from "../components/spaces/spaces";
import StepsHeader from "../components/steps-header/steps-header";
import WaterHeaterSystemModal from "../components/water-heater-system-modal/water-heater-system-modal";
import styles from "./build-system-advanced.module.scss";

const steps: ISteps[] = [
  {
    id: 1,
    stepName: "Add Devices",
    stepIcon: "devices_other",
  },
  {
    id: 2,
    stepName: "Add Usage Info",
    stepIcon: "autoplay",
  },
  { id: 3, stepName: "Assemble System", stepIcon: "widgets" },
];

const solarSystemTypesOption: Option[] = [
  {
    value: ESolarSystemType.BACKUP,
    label: "Backup",
  },
  {
    value: ESolarSystemType.OFF_GRID,
    label: "Off-Grid",
  },
  {
    value: ESolarSystemType.HYBRID,
    label: "Hybrid",
  },
];

const inverterPhaseTypeOptions: Option[] = [
  {
    value: EInverterPhaseType.SINGLE_PHASE,
    label: "1 Phase",
  },
  {
    value: EInverterPhaseType.THREE_PHASE,
    label: "3 Phase",
  },
  {
    value: EInverterPhaseType.NONE,
    label: "None",
  },
];

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];
interface ILocationState {
  isNavigateToAssembleSystem: boolean;
  systemType: ESolarSystemType;
  numberOfUsers: number;
  applicationType: EApplicationType;
  utilityOption: EUtilityOptionType;
  solarSolutionType: ESolarSolutionType;
  inverterPhaseType: EInverterPhaseType;
  estimationId: string;
  estimationToken: string;
}

const BuildSystemAdvanced = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ILocationState;
  const pathname = location.pathname;
  const billingSpaceFilter = useSelector(selectBillingSpaceFilter);
  const newClusterId = useSelector(selectNewClusterId);
  const country = useSelector(selectCountry);
  const auth = useSelector(selectAuth);

  const [selectedStep, setSelectedStep] = useState(1);
  const [monthlyEnergyNeed, setMonthlyEnergyNeed] = useState(0);
  const [monthlyEnergyNeedFromUser, setMonthlyEnergyNeedFromUser] = useState(0);
  const [maxPower, setMaxPower] = useState(0);
  const [maxPowerFromUser, setMaxPowerFromUser] = useState(0);
  const [editParameterType, setEditParameterType] =
    useState<EEnergyNeedEditParameter>(EEnergyNeedEditParameter.TITLE);
  const [editParameterValue, setEditParameterValue] = useState<number | string>(
    0,
  );
  const [title, setTitle] = useState("");
  const [estimatedValue, setEstimatedValue] = useState<number | undefined>(
    undefined,
  );
  const [estimatedUnit, setEstimatedUnit] = useState<string>("");
  const [showEditParameterModal, setShowEditParameterModal] = useState(false);
  const [dischargeRate, setDischargeRate] = useState(0);
  const [nightLoadFromUser, setNightLoadFromUser] = useState(0);
  const [nightLoad, setNightLoad] = useState(0);
  const [sunlightHours, setSunlightHours] = useState(5);
  const [usageState, setUsageState] = useState<IAddBillCalculatorProps>();
  const [isAddUsage, setIsAddUsage] = useState(false);
  const [estimatorDetails, setEstimatorDetails] = useState<IEstimatorDetails>({
    estimationId: "",
    estimationToken: "",
  });
  const [assembleSystemData, setAssembleSystemData] = useState<
    IViewSolarEstimationData | undefined
  >(undefined);
  const [isSpaceClusterAvailable, setIsSpaceClusterAvailable] = useState(false);
  const [isResetRecommendations, setIsResetRecommendations] = useState(false);
  const [selectedSystemOutput, setSelectedSystemOutput] =
    useState<IViewSolarEstimationOutputParams>({
      totalWattHours: 0,
      totalCost: 0,
      systemVoltage: 0,
      totalAmpHours: 0,
      nightLoadAmpHours: 0,
      batteryAmpHours: 0,
      maxPowerInWatt: 0,
      grandTotal: 0,
      totalAccessoriesCost: 0,
      laborCost: 0,
      totalLiters: 0,
    });
  const [userSelectedProducts, setUserSelectedProducts] = useState<
    IViewSolarUserSelectedProducts[]
  >([]);
  const [currency, setCurrency] = useState("");
  const [isDevicesAvailable, setIsDevicesAvailable] = useState(false);
  const [isSchedulesAvailable, setIsSchedulesAvailable] = useState(false);
  const [includeOtherFees, setIncludeOtherFees] = useState(false);
  const [estimationId, setEstimationId] = useState(0);
  const [comment, setComment] = useState("");
  const [spaceClusterId, setSpaceClusterId] = useState("");
  const [isReset, setIsReset] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [isScheduleDeleted, setIsScheduleDeleted] = useState(false);
  const [configurations, setConfigurations] = useState<Configurations>();
  const [solarSystemType, setSolarSystemType] = useState<Option>({
    ...solarSystemTypesOption[2],
  });
  const [inverterPhaseType, setInverterPhaseType] = useState<Option>({
    ...inverterPhaseTypeOptions[0],
  });
  const [applicationType, setApplicationType] = useState<Option>({
    ...applicationTypeOptions[0],
  });
  const [solarSolutionType, setSolarSolutionType] =
    useState<ESolarSolutionType>(ESolarSolutionType.BOTH);

  const [utilityOption, setUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const [waterDemand, setWaterDemand] = useState(0);
  const [isRemoveWaterHeaterSystem, setIsRemoveWaterHeaterSystem] =
    useState(false);
  const [isShowRemoveSolarSystemModal, setIsShowRemoveSolarSystemModal] =
    useState(false);
  const [
    isShowRemoveWaterHeaterSystemModal,
    setIsShowRemoveWaterHeaterSystemModal,
  ] = useState(false);
  const [isUpdateCurrentState, setIsUpdateCurrentState] = useState(false);
  const [isShowWaterHeaterSystemModal, setIsShowWaterHeaterSystemModal] =
    useState(false);
  const [isAddWaterHeaterSystem, setIsAddWaterHeaterSystem] = useState(false);
  const [controllerRequired, setControllerRequired] = useState(false);

  const [triggerAdvancedSolarEstimation, { isFetching: isEstimationLoading }] =
    useLazyAdvancedSolarEstimationCalculationQuery();
  const [
    triggerInitiateSolarEstimation,
    { isLoading: isLoadingInitiateSolarEstimation },
  ] = useInitiateSolarEstimationMutation();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();
  const [
    triggerCreateSolarEstimation,
    { isLoading: isLoadingCreateSolarEstimation },
  ] = useCreateSolarEstimationMutation();
  const [
    triggerLazyViewSolarEstimation,
    { isLoading: isLoadingViewEstimation },
  ] = useLazyViewSolarEstimationQuery();

  useEffect(() => {
    if (solarSystemType.value) {
      setHasChanged(true);
    }
  }, [solarSystemType.value]);

  useEffect(() => {
    const advancedSave = localStorage.getItem("advancedSave");

    if (advancedSave) {
      const parsedAdvancedSave: IAdvancedSave = JSON.parse(advancedSave);

      navigate(location.pathname, {
        replace: true,
        state: {
          systemType: parsedAdvancedSave.systemType,
          inverterPhaseType: parsedAdvancedSave.inverterPhaseType,
          numberOfUsers: parsedAdvancedSave.numberOfUsers,
          applicationType: parsedAdvancedSave.applicationType,
          utilityOption: parsedAdvancedSave.utilityOption,
          solarSolutionType: parsedAdvancedSave.estimationType,
          estimationId: parsedAdvancedSave.estimationId,
        },
      });
      localStorage.removeItem("advancedSave");
    }
  }, []);

  useEffect(() => {
    const unparsedEstimatorDetails = localStorage.getItem(
      ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
    );

    if (unparsedEstimatorDetails) {
      const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
        unparsedEstimatorDetails,
      );
      setEstimatorDetails(parsedEstimatorDetails);
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: parsedEstimatorDetails.estimationId,
            }
          : {
              estimatorToken: parsedEstimatorDetails.estimationToken,
            },
      )
        .unwrap()
        .then((res) => {
          setEstimationId(res.estimationView.estimationId);
          setSolarSolutionType(res.estimationView.estimationType);
          setNumberOfUsers(
            res.estimationView.data.inputParams.numberOfUsers || 0,
          );
          setWaterDemand(res.estimationView.data.inputParams.waterDemand || 0);

          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === res.estimationView.data.inputParams.systemType,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === res.estimationView.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === res.estimationView.utilityOptionType,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === res.estimationView.applicationType,
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          if (res.estimationView.spaceCluster) {
            setIsSpaceClusterAvailable(true);
            const selectedSpaceCluster = res.estimationView.spaceCluster;

            setDischargeRate(
              res.estimationView.data.inputParams.dischargeRatePercentage || 0,
            );
            setSunlightHours(
              res.estimationView.data.inputParams.sunlightHours || 5,
            );

            const { id: clusterId, rootSpace } = selectedSpaceCluster;

            const selectedSpace = {
              id: rootSpace.id,
              clusterId: clusterId,
              name: rootSpace.name,
            };

            const selectedSpaceDetails = {
              spaceClusterId: clusterId,
              spaceId: rootSpace.id,
            };
            setTitle((ps) => res.estimationView.title || ps);
            setIncludeOtherFees(res.estimationView.installationCostRequested);
            setControllerRequired(res.estimationView.controllerRequired);
            setConfigurations(
              res.estimationView.data.preferences as Configurations,
            );
            dispatch(setBillingSpaceFilter({ ...selectedSpaceCluster }));
            dispatch(setSelectedFilterSpace(selectedSpace));
            dispatch(
              setBillCalculatorFiltersSpaceAndSpaceDetails({
                selectedSpace: { ...rootSpace, clusterId },
                selectedSpaceDetails,
              }),
            );
            return;
          }

          setIsSpaceClusterAvailable(false);
          navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES);
        })
        .catch(() => {
          setEstimationId(0);
          setIsSpaceClusterAvailable(false);
          setTitle((ps) => ps);
          setIncludeOtherFees(false);
          navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES);
        });
      return;
    }

    if (locationState && locationState?.estimationId) {
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: locationState.estimationId,
            }
          : {
              estimatorToken: locationState?.estimationToken,
            },
      )
        .unwrap()
        .then((res: ViewSolarEstimationResponseDTO) => {
          setEstimationId(res.estimationView.estimationId);
          setTitle(res.estimationView.title || "");
          setEstimatorDetails({
            estimationId: res.estimationView.id,
            estimationToken: auth.token || locationState?.estimationToken,
          });
          setNumberOfUsers(
            res.estimationView.data.inputParams.numberOfUsers || 0,
          );
          setWaterDemand(res.estimationView.data.inputParams.waterDemand || 0);
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            },
          );
          setIncludeOtherFees(res.estimationView.installationCostRequested);
          setControllerRequired(res.estimationView.controllerRequired);

          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === locationState?.systemType,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === locationState.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === res.estimationView.utilityOptionType,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === res.estimationView.applicationType,
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          if (res.estimationView.data.preferences) {
            setConfigurations(
              res.estimationView.data.preferences as Configurations,
            );
          }

          if (res.estimationView.spaceCluster) {
            setIsSpaceClusterAvailable(true);
            const selectedSpaceCluster = res.estimationView.spaceCluster;

            const { id: clusterId, rootSpace } = selectedSpaceCluster;

            const selectedSpace = {
              id: rootSpace.id,
              clusterId: clusterId,
              name: rootSpace.name,
            };

            const selectedSpaceDetails = {
              spaceClusterId: clusterId,
              spaceId: rootSpace.id,
            };
            dispatch(setBillingSpaceFilter({ ...selectedSpaceCluster }));
            dispatch(setSelectedFilterSpace(selectedSpace));
            dispatch(
              setBillCalculatorFiltersSpaceAndSpaceDetails({
                selectedSpace: { ...rootSpace, clusterId },
                selectedSpaceDetails,
              }),
            );
            return;
          }

          setIsSpaceClusterAvailable(false);
        });

      return;
    }

    if (locationState) {
      triggerInitiateSolarEstimation({
        estimationType: locationState.solarSolutionType,
        applicationType:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.applicationType
            : null,
        utilityOptionType:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.utilityOption
            : null,
        solarEstimationType: EEstimationType.ADVANCED,
        systemType: locationState.systemType as ESolarSystemType,
        inverterPhaseType:
          locationState.inverterPhaseType as EInverterPhaseType,
        monthlyRequiredEnergy: null,
        maxRequiredPower: null,
        numberOfUsers:
          locationState.solarSolutionType === ESolarSolutionType.BOTH
            ? locationState.numberOfUsers
            : null,
        nightLoad: null,
        sunlightHours: null,
        countryCode: country,
      })
        .unwrap()
        .then((res) => {
          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === locationState?.systemType,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === locationState.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === locationState.utilityOption,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === locationState.applicationType,
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }

          setNumberOfUsers(locationState.numberOfUsers);
          setSolarSolutionType(locationState.solarSolutionType);

          setEstimatorDetails({
            estimationId: res.id,
            estimationToken: res.token,
          });

          localStorage.setItem(
            ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
            JSON.stringify({
              estimationId: res.id,
              estimationToken: res.token,
            }),
          );

          triggerLazyViewSolarEstimation(
            auth.token
              ? {
                  estimationId: res.id,
                }
              : {
                  estimatorToken: res.token,
                },
          )
            .unwrap()
            .then((viewRes) => {
              setEstimationId(viewRes.estimationView.estimationId);
              setConfigurations(
                viewRes.estimationView.data.preferences as Configurations,
              );
              setDischargeRate(
                viewRes.estimationView.data.inputParams
                  .dischargeRatePercentage ?? 0,
              );
              navigate(location.pathname, { replace: true, state: undefined });
            })
            .catch(() => {
              setEstimationId(0);
            });
        });
    }
  }, [
    newClusterId,
    triggerInitiateSolarEstimation,
    triggerLazyViewSolarEstimation,
    locationState,
    isUpdateCurrentState,
    billingSpaceFilter.isSpaceClusterListChanged,
  ]);

  useEffect(() => {
    if (
      !isAddUsage &&
      billingSpaceFilter.spaceCluster.id &&
      billingSpaceFilter.spaceCluster.rootSpace.id
    ) {
      triggerAdvancedSolarEstimation({
        spaceClusterId: billingSpaceFilter.spaceCluster.id,
        subRootSpaceId: billingSpaceFilter.spaceCluster.rootSpace.id,
        scheduledYear: new Date().getFullYear(),
        scheduledMonth: new Date().getMonth() + 1,
        zoneOffSet: offsetToString(moment().utcOffset()),
      })
        .unwrap()
        .then((res) => {
          setMonthlyEnergyNeed(res.spaceClusterEnergyView.energyInUnits);
          setMaxPower(res.maxLoad);
          setNightLoad(res.nightLoad);
        })
        .catch(() => {
          setMonthlyEnergyNeed(0);
          setMaxPower(0);
          setNightLoad(0);
        });
    }
  }, [
    isAddUsage,
    billingSpaceFilter,
    triggerAdvancedSolarEstimation,
    isScheduleDeleted,
  ]);

  useEffect(() => {
    if (!pathname.includes("assemble-system")) return;

    const { estimationId, estimationToken } = estimatorDetails;
    const spaceClusterId = billingSpaceFilter.spaceCluster?.id;

    if (!estimationId || !estimationToken) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails,
        );
        setEstimatorDetails(parsedEstimatorDetails);
      }

      return;
    }

    if (!spaceClusterId || !country) {
      return;
    }

    if (
      (monthlyEnergyNeed === 0 && monthlyEnergyNeedFromUser === 0) ||
      (maxPower === 0 && maxPowerFromUser === 0)
    ) {
      return;
    }

    const fetchPreferences = async () => {
      setAssembleSystemData(undefined);
      try {
        let brandPriority: ESolarSimBrands[] = [];

        if (configurations) {
          if (Array.isArray(configurations.BRAND_PRIORITY_LIST)) {
            brandPriority = configurations.BRAND_PRIORITY_LIST.map(
              (bp) => bp.brand,
            );
          }
        }

        const brandPriorityPreference = configurations
          ? Boolean(configurations.BRAND_PRIORITY)
          : false;
        const optimizeForCost = configurations
          ? Boolean(configurations.OPTIMIZE_FOR_COST)
          : false;
        const preferHighEfficiencyPanels = configurations
          ? Boolean(configurations.PREFER_HIGH_EFFICIENCY_PANELS)
          : false;
        const recommendExpandableSystems = configurations
          ? Boolean(configurations.RECOMMEND_EXPANDABLE_SYSTEMS)
          : false;

        if (brandPriority.length === 0) {
          return;
        }

        await triggerCreateSolarEstimation({
          estimationId: auth.token ? estimationId : "",
          estimatorToken: auth.token ? "" : estimationToken,
          title: title ? title : null,
          estimationType: solarSolutionType,
          applicationType:
            solarSolutionType === ESolarSolutionType.BOTH
              ? (applicationType.value as EApplicationType)
              : null,
          utilityOptionType:
            solarSolutionType === ESolarSolutionType.BOTH
              ? (utilityOption.value as EUtilityOptionType)
              : null,
          solarEstimationType: EEstimationType.ADVANCED,
          systemType: solarSystemType.value as ESolarSystemType,
          inverterPhaseType:
            solarSystemType.value === ESolarSystemType.OFF_GRID
              ? EInverterPhaseType.NONE
              : (inverterPhaseType.value as EInverterPhaseType),
          monthlyRequiredEnergy: monthlyEnergyNeed + monthlyEnergyNeedFromUser,
          maxRequiredPower: maxPower + maxPowerFromUser * 1000,
          numberOfUsers:
            solarSolutionType === ESolarSolutionType.BOTH
              ? numberOfUsers
              : null,
          nightLoad: nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad,
          sunlightHours: sunlightHours,
          estimationSpaceClusterId: spaceClusterId,
          estimationBasicScheduleId: null,
          isDraft: false,
          brandPriority,
          brandPriorityPreference,
          optimizeForCost,
          preferHighEfficiencyPanels,
          recommendExpandableSystems,
          countryCode: country,
          hasChanged: hasChanged || null,
          controllerRequired: controllerRequired,
        }).unwrap();

        setHasChanged(false);

        const viewRes = await triggerLazyViewSolarEstimation(
          auth.token
            ? {
                estimationId: estimationId,
              }
            : {
                estimatorToken: estimationToken,
              },
        ).unwrap();

        setAssembleSystemData({ ...viewRes.estimationView.data });
        setDischargeRate(
          viewRes.estimationView.data.inputParams.dischargeRatePercentage ?? 0,
        );
        setSelectedSystemOutput(
          viewRes.estimationView.data.outputParams || {
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          },
        );
      } catch {
        setAssembleSystemData(undefined);
      }
    };

    fetchPreferences();
  }, [
    pathname,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
    title,
    monthlyEnergyNeed,
    monthlyEnergyNeedFromUser,
    maxPower,
    maxPowerFromUser,
    nightLoad,
    sunlightHours,
    nightLoadFromUser,
    billingSpaceFilter.spaceCluster.id,
    isResetRecommendations,
    country,
    configurations,
    solarSystemType,
    inverterPhaseType,
    solarSolutionType,
    applicationType,
    utilityOption,
    numberOfUsers,
    controllerRequired,
  ]);

  useEffect(() => {
    const pathnameSlices = pathname.split("/");

    if (!pathnameSlices.includes("usage-info")) {
      setIsAddUsage(false);
    }

    if (!pathnameSlices.includes("assemble-system")) {
      setAssembleSystemData(undefined);
    }

    if (pathnameSlices.includes("devices")) {
      setSelectedStep(1);
      return;
    }

    if (pathnameSlices.includes("usage-info")) {
      setSelectedStep(2);
      return;
    }

    if (pathnameSlices.includes("assemble-system")) {
      setSelectedStep(3);
      return;
    }

    if (pathnameSlices.includes("system-summary")) {
      setSelectedStep(4);
    }
  }, [pathname]);

  useEffect(() => {
    if (!pathname.includes("system-summary")) return;

    const { estimationId, estimationToken } = estimatorDetails;

    if (!estimationId || !estimationToken) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails,
        );
        setEstimatorDetails(parsedEstimatorDetails);
      }

      return;
    }

    triggerLazyViewSolarEstimation(
      auth.token
        ? {
            estimationId: estimatorDetails.estimationId,
          }
        : {
            estimatorToken: estimatorDetails.estimationToken,
          },
    )
      .unwrap()
      .then((res) => {
        setSpaceClusterId(res.estimationView.spaceClusterId || "");
        setComment(res.estimationView.adminComment || "");
        setUserSelectedProducts(
          res.estimationView.data.userSelectedProducts || [],
        );
        setAssembleSystemData({ ...res.estimationView.data });
        setCurrency(res.estimationView.currency);
        setSelectedSystemOutput(
          res.estimationView.data.outputParams || {
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          },
        );
        setConfigurations(
          res.estimationView.data.preferences as Configurations,
        );
      })
      .catch(() => {
        setSpaceClusterId("");
        setComment("");
        setUserSelectedProducts([]);
        setAssembleSystemData(undefined);
        setCurrency("");
        setSelectedSystemOutput({
          totalWattHours: 0,
          totalCost: 0,
          systemVoltage: 0,
          totalAmpHours: 0,
          nightLoadAmpHours: 0,
          batteryAmpHours: 0,
          maxPowerInWatt: 0,
          grandTotal: 0,
          totalAccessoriesCost: 0,
          laborCost: 0,
          totalLiters: 0,
        });
      });
  }, [
    pathname,
    estimatorDetails.estimationToken,
    estimatorDetails.estimationId,
  ]);

  useEffect(() => {
    if (
      isDevicesAvailable &&
      locationState?.isNavigateToAssembleSystem &&
      pathname.includes("devices")
    ) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_USAGE_INFO, {
        state: {
          isNavigateToAssembleSystem: true,
        },
      });
      return;
    }

    if (
      isSchedulesAvailable &&
      locationState?.isNavigateToAssembleSystem &&
      pathname.includes("usage-info")
    ) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_ASSEMBLE_SYSTEM, {
        state: {
          isNavigateToAssembleSystem: false,
        },
      });
    }
  }, [
    locationState?.isNavigateToAssembleSystem,
    isSchedulesAvailable,
    isDevicesAvailable,
    pathname,
  ]);

  const onParameterEdit = (type: EEnergyNeedEditParameter) => {
    setEditParameterType(type);
    let editValue: number | string;
    if (type === EEnergyNeedEditParameter.TITLE) {
      editValue = title;
    } else if (type === EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED) {
      editValue = monthlyEnergyNeedFromUser;
    } else if (type === EEnergyNeedEditParameter.MAX_POWER) {
      editValue = maxPowerFromUser;
    } else if (type === EEnergyNeedEditParameter.NO_OF_USERS) {
      editValue = numberOfUsers;
    } else if (type === EEnergyNeedEditParameter.SUNLIGHT_HOURS) {
      editValue = sunlightHours;
    } else if (nightLoadFromUser > 0) {
      editValue = nightLoadFromUser;
    } else {
      editValue = nightLoad;
    }
    setEditParameterValue(editValue);
    let estimatedVal: number | undefined;
    if (type === EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED) {
      estimatedVal = monthlyEnergyNeed;
    } else if (type === EEnergyNeedEditParameter.MAX_POWER) {
      estimatedVal = maxPower / 1000;
    } else if (type === EEnergyNeedEditParameter.NIGHT_LOAD) {
      estimatedVal = nightLoad;
    } else {
      estimatedVal = undefined;
    }
    setEstimatedValue(estimatedVal);
    const getEstimatedUnit = (
      parameterType: EEnergyNeedEditParameter,
    ): string => {
      if (parameterType === EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED) {
        return "Units";
      }
      if (parameterType === EEnergyNeedEditParameter.MAX_POWER) {
        return "kW";
      }
      return "";
    };

    setEstimatedUnit(getEstimatedUnit(type));
    setShowEditParameterModal(true);
  };

  const onEditParameter = (type: EEnergyNeedEditParameter, value: string) => {
    let newValue: string | number;
    if (type === EEnergyNeedEditParameter.TITLE) {
      newValue = value;
    } else if (value.trim() === "") {
      newValue = 0;
    } else {
      newValue = value;
    }

    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        setTitle(newValue as string);
        break;
      case EEnergyNeedEditParameter.MONTHLY_ENERGY_NEED: {
        const previousValue = monthlyEnergyNeedFromUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setMonthlyEnergyNeedFromUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.MAX_POWER: {
        const previousValue = maxPowerFromUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setMaxPowerFromUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.NIGHT_LOAD: {
        const previousValue = nightLoadFromUser;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNightLoadFromUser(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.SUNLIGHT_HOURS: {
        const previousValue = sunlightHours;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setSunlightHours(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }
        break;
      }
      case EEnergyNeedEditParameter.NO_OF_USERS: {
        const previousValue = numberOfUsers;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNumberOfUsers(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue),
          );
          setIsReset(true);
        }

        break;
      }
      default:
        break;
    }
    setShowEditParameterModal(false);
  };

  const onEventChange = (state: IAddBillCalculatorProps) => {
    setUsageState(state);
    setIsAddUsage(true);
  };

  const onStepClick = async (stepNumber: number) => {
    if (selectedStep === stepNumber) return;

    if (stepNumber < selectedStep && stepNumber === 1) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES);
      return;
    }

    if (stepNumber < selectedStep && stepNumber === 2) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_USAGE_INFO);
      return;
    }

    if (stepNumber < selectedStep && stepNumber === 3) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_ASSEMBLE_SYSTEM);
      return;
    }

    if (stepNumber < selectedStep && stepNumber === 4) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_SYSTEM_SUMMARY);
      return;
    }

    if (!isSpaceClusterAvailable && selectedStep === 1) {
      showErrorMessage("Please add a space before continuing");
      return;
    }

    if (!isDevicesAvailable && selectedStep === 1) {
      showErrorMessage("Please add a device before continuing");
      return;
    }

    if (!title.trim()) {
      showErrorMessage("Please provide a title before continuing");
      return;
    }

    if (!isSchedulesAvailable && stepNumber > 2) {
      showErrorMessage("Please add a schedule before continuing");
      return;
    }

    await onSave(
      solarSolutionType,
      applicationType.value as EApplicationType,
      utilityOption.value as EUtilityOptionType,
      solarSystemType.value as ESolarSystemType,
      inverterPhaseType.value as EInverterPhaseType,
      monthlyEnergyNeed,
      maxPower,
      numberOfUsers,
      nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad,
      sunlightHours,
      monthlyEnergyNeedFromUser,
      maxPowerFromUser,
    );

    if (stepNumber === 1 && selectedStep !== 1) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES);
      return;
    }

    if (stepNumber === 2 && selectedStep !== 2) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_USAGE_INFO);
      return;
    }

    if (stepNumber === 3 && selectedStep !== 3) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_ASSEMBLE_SYSTEM);
      return;
    }

    if (stepNumber === 4 && selectedStep !== 4) {
      navigate(AppRoute.DESIGN_SYSTEM_ADVANCED_SYSTEM_SUMMARY);
    }
  };

  const onSave = async (
    estimationType: ESolarSolutionType,
    applicationType: EApplicationType,
    utilityOptionType: EUtilityOptionType,
    systemType: ESolarSystemType,
    inverterPhaseType: EInverterPhaseType,
    monthlyEnergyNeed: number,
    maxPower: number,
    numberOfUsers: number,
    nightLoad: number,
    sunlightHours: number,
    monthlyEnergyNeedFromUser: number,
    maxPowerFromUser: number,
  ) => {
    if (
      !title.trim() &&
      !isRemoveWaterHeaterSystem &&
      !isAddWaterHeaterSystem
    ) {
      showErrorMessage("Please provide a title to save the estimation");
      return;
    }

    try {
      if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
        return;
      }

      await triggerUpdateEstimation({
        estimationId: auth.token ? estimatorDetails.estimationId : "",
        estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
        title: title ? title : null,
        estimationType,
        applicationType:
          estimationType === ESolarSolutionType.BOTH ? applicationType : null,
        utilityOptionType:
          estimationType === ESolarSolutionType.BOTH ? utilityOptionType : null,
        solarEstimationType: EEstimationType.ADVANCED,
        systemType,
        inverterPhaseType:
          systemType === ESolarSystemType.OFF_GRID
            ? EInverterPhaseType.NONE
            : inverterPhaseType,
        monthlyRequiredEnergy: monthlyEnergyNeed + monthlyEnergyNeedFromUser,
        maxRequiredPower: maxPower + maxPowerFromUser * 1000,
        numberOfUsers:
          estimationType === ESolarSolutionType.BOTH ? numberOfUsers : null,
        nightLoad: nightLoad,
        sunlightHours: sunlightHours,
        estimationSpaceClusterId: billingSpaceFilter.spaceCluster.id
          ? billingSpaceFilter.spaceCluster.id
          : null,
        estimationBasicScheduleId: null,
        isDraft: false,
        countryCode: country,
        installationCostRequested: includeOtherFees,
      })
        .unwrap()
        .then(() => {
          if (isRemoveWaterHeaterSystem) {
            showSuccessMessage("Water Heater System Deleted Successfully");
          }

          if (isAddWaterHeaterSystem) {
            showSuccessMessage("Water Heater System Added Successfully");
          }

          if (!isRemoveWaterHeaterSystem && !isAddWaterHeaterSystem) {
            showSuccessMessage("Estimation Saved Successfully");
          }

          setIsUpdateCurrentState((ps) => !ps);
          setIsRemoveWaterHeaterSystem(false);
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsShowRemoveSolarSystemModal(false);
          setIsAddWaterHeaterSystem(false);
          setIsShowWaterHeaterSystemModal(false);
        });
    } catch {
      showErrorMessage("Failed to save");
    }
  };

  return (
    <>
      <StepsHeader
        steps={steps}
        selectedStep={selectedStep}
        onStepClick={onStepClick}
        onSave={() => {
          onSave(
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad,
            sunlightHours,
            monthlyEnergyNeedFromUser,
            maxPowerFromUser,
          );
        }}
        isApproved={false}
        onChangeMode={() => {
          if (!auth.token) {
            navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO);
            return;
          }

          navigate(AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO, {
            state: {
              numberOfUsers,
              applicationType: applicationType.value as EApplicationType,
              utilityOption: utilityOption.value as EUtilityOptionType,
              solarSolutionType,
              systemType: solarSystemType.value as ESolarSystemType,
              inverterPhaseType: inverterPhaseType.value as EInverterPhaseType,
            },
          });
        }}
      />
      <div className={`py-3 ${styles.mainContent}`}>
        <div className="estimation-container rounded-3">
          {!isAddUsage && (
            <Row
              className={`align-items-center justify-content-between py-2 px-4 ${
                solarSolutionType === ESolarSolutionType.BOTH ? `mb-2` : ``
              }`}
            >
              <Col className="col-auto">
                <Row>
                  <Col className="text-color-3 font-size-14">
                    #{estimationId}
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <Col className="col-auto title-2 font-weight-600 font-size18 pe-0">
                    {title === "" ? "Title Goes Here" : title}
                  </Col>
                  <Col className="col-auto ps-1">
                    <MaterialIcon
                      icon="border_color"
                      color="var(--color-edit-icon-2)"
                      size={12}
                      className="cursor-pointer"
                      onClick={() => {
                        onParameterEdit(EEnergyNeedEditParameter.TITLE);
                      }}
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                className={
                  solarSolutionType === ESolarSolutionType.BOTH &&
                  !pathname.includes("devices")
                    ? `col-12 mt-2`
                    : `col-12 col-xxl-auto mt-3 mt-xxl-0`
                }
              >
                <Row className="align-items-stretch gx-2">
                  {!pathname.includes("devices") && (
                    <Col
                      className={
                        solarSolutionType === ESolarSolutionType.BOTH
                          ? `col-12 col-xxl-7`
                          : ``
                      }
                    >
                      <SelectedSolarSystemCard
                        monthlyEnergyNeed={monthlyEnergyNeed}
                        maxPower={maxPower}
                        dischargeRate={dischargeRate}
                        nightLoad={
                          nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad
                        }
                        sunlightHours={sunlightHours}
                        monthlyEnergyNeedFromUser={monthlyEnergyNeedFromUser}
                        maxPowerFromUser={maxPowerFromUser}
                        totalWattHours={selectedSystemOutput.totalWattHours}
                        maxPowerInWatt={selectedSystemOutput.maxPowerInWatt}
                        onParameterEdit={onParameterEdit}
                        solarSystemType={solarSystemType}
                        onChangeSolarSystemType={(option: Option) => {
                          setSolarSystemType(option);
                          setIsReset(true);
                        }}
                        inverterPhaseType={inverterPhaseType}
                        onChangeInverterPhaseType={(option: Option) => {
                          setInverterPhaseType(option);
                          setIsReset(true);
                        }}
                        isHideDeleteIcon={
                          solarSolutionType !== ESolarSolutionType.BOTH ||
                          selectedStep === 4
                        }
                        onDeleteSolarSystem={() =>
                          setIsShowRemoveSolarSystemModal(true)
                        }
                        isHideEditIcon={selectedStep === 4}
                      />
                    </Col>
                  )}
                  <Col
                    className={
                      solarSolutionType === ESolarSolutionType.BOTH &&
                      !pathname.includes("devices")
                        ? `col-12 col-xxl-5 mt-3 mt-xxl-0`
                        : `col-12 col-xxl-auto mt-3 mt-xxl-0`
                    }
                  >
                    {solarSolutionType === ESolarSolutionType.BOTH ? (
                      <SelectedWaterHeaterSystemCard
                        onParameterEdit={onParameterEdit}
                        waterDemand={waterDemand}
                        totalLiters={selectedSystemOutput.totalLiters || 0}
                        numberOfUsers={numberOfUsers}
                        applicationType={applicationType}
                        utilityOption={utilityOption}
                        onApplicationTypeChange={(option: Option) => {
                          setApplicationType(option);
                          setIsReset(true);
                        }}
                        onUtilityOptionChange={(option: Option) => {
                          setUtilityOption(option);
                          setIsReset(true);
                        }}
                        onDeleteWaterHeaterSystem={() => {
                          setIsShowRemoveWaterHeaterSystemModal(true);
                          setIsRemoveWaterHeaterSystem(true);
                        }}
                        isHideDeleteIcon={selectedStep === 4}
                        isHideEditIcon={selectedStep === 4}
                      />
                    ) : (
                      <>
                        {selectedStep === 4 ? (
                          <></>
                        ) : (
                          <IncludeSystemCard
                            icon="water_drop"
                            text="Include Solar Water Heating System as Well?"
                            onClick={() => {
                              setIsShowWaterHeaterSystemModal(true);
                              setIsAddWaterHeaterSystem(true);
                            }}
                          />
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          {(() => {
            let content;
            if (selectedStep === 1) {
              content = (
                <Spaces
                  isSpaceClusterAvailable={isSpaceClusterAvailable}
                  estimationId={estimatorDetails.estimationId}
                  setIsDevicesAvailable={setIsDevicesAvailable}
                />
              );
            } else if (selectedStep === 2) {
              if (isAddUsage && usageState) {
                content = (
                  <AddUpdateBillCalculator
                    {...usageState}
                    setIsAddUsage={setIsAddUsage}
                  />
                );
              } else {
                content = (
                  <BillCalculator
                    onEventChange={onEventChange}
                    setIsSchedulesAvailable={setIsSchedulesAvailable}
                    isScheduleDeleted={isScheduleDeleted}
                    setIsScheduleDeleted={setIsScheduleDeleted}
                  />
                );
              }
            } else if (selectedStep === 3) {
              content = (
                <AssembleSystem
                  assembleSystemData={assembleSystemData}
                  estimatorDetails={estimatorDetails}
                  selectedSystemOutput={selectedSystemOutput}
                  setSelectedSystemOutput={setSelectedSystemOutput}
                  setIsResetRecommendations={setIsResetRecommendations}
                  configurations={configurations}
                  sendConfigurations={(configurations: Configurations) => {
                    setConfigurations({ ...configurations });
                  }}
                  isReset={isReset}
                  setIsReset={setIsReset}
                  setHasChanged={setHasChanged}
                  estimationNo={estimationId}
                  setDischargeRate={setDischargeRate}
                  estimationType={solarSolutionType}
                  setWaterDemand={setWaterDemand}
                  controllerRequired={controllerRequired}
                  setControllerRequired={setControllerRequired}
                  systemType={solarSystemType.value as ESolarSystemType}
                  isDisabledControllerSwitch={
                    isLoadingCreateSolarEstimation ||
                    isLoadingViewEstimation ||
                    isLoadingUpdateEstimation
                  }
                />
              );
            } else {
              content = (
                <ApprovedSystem
                  userSelectedProducts={userSelectedProducts}
                  assembleSystemData={assembleSystemData}
                  onSave={() => {
                    onSave(
                      solarSolutionType,
                      applicationType.value as EApplicationType,
                      utilityOption.value as EUtilityOptionType,
                      solarSystemType.value as ESolarSystemType,
                      inverterPhaseType.value as EInverterPhaseType,
                      monthlyEnergyNeed,
                      maxPower,
                      numberOfUsers,
                      nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad,
                      sunlightHours,
                      monthlyEnergyNeedFromUser,
                      maxPowerFromUser,
                    );
                  }}
                  currency={currency}
                  estimatorDetails={estimatorDetails}
                  includeOtherFees={includeOtherFees}
                  setIncludeOtherFees={setIncludeOtherFees}
                  comment={comment}
                  title={title}
                  country={country}
                  monthlyRequiredEnergy={
                    monthlyEnergyNeed + monthlyEnergyNeedFromUser
                  }
                  maxRequiredPower={maxPower + maxPowerFromUser * 1000}
                  systemType={solarSystemType.value as ESolarSystemType}
                  inverterPhaseType={
                    inverterPhaseType.value as EInverterPhaseType
                  }
                  nightLoad={nightLoad}
                  sunlightHours={sunlightHours}
                  estimationSpaceClusterId={spaceClusterId}
                  estimationNo={estimationId}
                  estimationType={solarSolutionType}
                  applicationType={applicationType.value as EApplicationType}
                  utilityOptionType={utilityOption.value as EUtilityOptionType}
                  numberOfUsers={numberOfUsers}
                />
              );
            }
            return content;
          })()}
        </div>
      </div>
      <EditParameterModal
        show={showEditParameterModal}
        onCancel={() => {
          setShowEditParameterModal(false);
        }}
        onClose={() => {
          setShowEditParameterModal(false);
        }}
        type={editParameterType}
        currentValue={editParameterValue}
        onEditParameter={onEditParameter}
        estimated={estimatedValue}
        unit={estimatedUnit}
      />
      <RemoveSolarSystemModal
        show={isShowRemoveSolarSystemModal}
        onClose={() => setIsShowRemoveSolarSystemModal(false)}
        onCancel={() => setIsShowRemoveSolarSystemModal(false)}
        onConfirm={() => {
          triggerUpdateEstimation({
            estimationId: auth.token ? estimatorDetails.estimationId : "",
            estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
            title: title ? title : null,
            estimationType: ESolarSolutionType.WATER_HEATING_SYSTEM,
            applicationType: applicationType.value as EApplicationType,
            utilityOptionType: utilityOption.value as EUtilityOptionType,
            solarEstimationType: null,
            systemType: null,
            inverterPhaseType: null,
            monthlyRequiredEnergy: null,
            maxRequiredPower: null,
            numberOfUsers: numberOfUsers,
            nightLoad: null,
            sunlightHours: null,
            estimationSpaceClusterId: null,
            estimationBasicScheduleId: null,
            isDraft: false,
            countryCode: country,
            installationCostRequested: includeOtherFees,
          })
            .unwrap()
            .then(() => {
              showSuccessMessage("Solar Energy System Deleted Successfully");
              setIsShowRemoveSolarSystemModal(false);

              if (auth.token) {
                navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM, {
                  state: {
                    estimationId: estimatorDetails.estimationId,
                    estimationToken: estimatorDetails.estimationToken,
                  },
                });
                return;
              }

              navigate(
                AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
                {
                  state: {
                    estimationId: estimatorDetails.estimationId,
                    estimationToken: estimatorDetails.estimationToken,
                  },
                },
              );
            });
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <RemoveWaterHeaterSystemModal
        show={isShowRemoveWaterHeaterSystemModal}
        onClose={() => {
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsRemoveWaterHeaterSystem(false);
        }}
        onCancel={() => {
          setIsShowRemoveWaterHeaterSystemModal(false);
          setIsRemoveWaterHeaterSystem(false);
        }}
        onConfirm={() => {
          onSave(
            ESolarSolutionType.SOLAR_ENERGY_SYSTEM,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad,
            sunlightHours,
            monthlyEnergyNeedFromUser,
            maxPowerFromUser,
          );
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <WaterHeaterSystemModal
        show={isShowWaterHeaterSystemModal}
        onClose={() => {
          setIsShowWaterHeaterSystemModal(false);
          setIsAddWaterHeaterSystem(false);
        }}
        onCancel={() => {
          setIsShowWaterHeaterSystemModal(false);
          setIsAddWaterHeaterSystem(false);
        }}
        onSave={(applicationType, utilityOption, numberOfUsers) => {
          onSave(
            ESolarSolutionType.BOTH,
            applicationType,
            utilityOption,
            solarSystemType.value as ESolarSystemType,
            inverterPhaseType.value as EInverterPhaseType,
            monthlyEnergyNeed,
            maxPower,
            numberOfUsers,
            nightLoadFromUser > 0 ? nightLoadFromUser : nightLoad,
            sunlightHours,
            monthlyEnergyNeedFromUser,
            maxPowerFromUser,
          );
        }}
        isLoading={isLoadingUpdateEstimation}
      />
      <SpinnerModal
        show={
          isEstimationLoading ||
          isLoadingInitiateSolarEstimation ||
          isLoadingUpdateEstimation ||
          isLoadingCreateSolarEstimation ||
          isLoadingViewEstimation
        }
      />
    </>
  );
};

export default BuildSystemAdvanced;
